import React, { useState } from 'react'
import { getStripe, createDonationCheckout } from '../utils/stripe'

interface DonateModalProps {
  isOpen: boolean
  onClose: () => void
}

const DONATION_AMOUNTS = [
  { label: '$5', cents: 500 },
  { label: '$10', cents: 1000 },
  { label: '$25', cents: 2500 },
  { label: '$50', cents: 5000 },
]

const DonateModal: React.FC<DonateModalProps> = ({ isOpen, onClose }) => {
  const [loadingCents, setLoadingCents] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleDonate = async (amountCents: number) => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      setError('Please sign in to donate')
      return
    }

    setLoadingCents(amountCents)
    setError(null)

    try {
      const session = await createDonationCheckout(token, amountCents)
      const stripe = await getStripe()
      if (!stripe) {
        throw new Error('Failed to load Stripe')
      }

      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: session.session_id,
      })
      if (stripeError) {
        throw stripeError
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Donation failed')
      setLoadingCents(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Support the site</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              ineedcarbonbuckets is free to use. Voluntary donations help cover scraping and hosting costs.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close donation modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {DONATION_AMOUNTS.map(({ label, cents }) => (
            <button
              key={cents}
              onClick={() => handleDonate(cents)}
              disabled={loadingCents !== null}
              className="px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:border-primary-300 dark:hover:border-primary-700 text-gray-900 dark:text-white font-semibold transition-colors disabled:opacity-50"
            >
              {loadingCents === cents ? 'Redirecting...' : label}
            </button>
          ))}
        </div>

        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          Secure payment via Stripe. Donations are optional and do not affect access.
        </p>
      </div>
    </div>
  )
}

export default DonateModal
